import React, { useEffect, useState } from "react";
import axios from "../services/api";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { FiEdit, FiTrash2, FiCheckCircle, FiXCircle } from "react-icons/fi";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ id: null, name: "", is_active: true });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/companies/?include_inactive=true");
      setCompanies(res.data);
    } catch {
      toast.error("Failed to fetch companies");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`/companies/${form.id}`, form);
        toast.success("Company updated");
      } else {
        await axios.post("/companies/", { name: form.name });
        toast.success("Company added");
      }
      resetForm();
      fetchCompanies();
    } catch {
      toast.error("Operation failed");
    }
    setLoading(false);
  };

  const handleEdit = (company) => {
    setForm({
      id: company.id,
      name: company.name,
      is_active: company.is_active,
    });
    setEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this company?")) return;
    setLoading(true);
    try {
      await axios.delete(`/companies/${id}`);
      toast.success("Company deleted");
      fetchCompanies();
    } catch {
      toast.error("Failed to delete company");
    }
    setLoading(false);
  };

  const handleToggleStatus = async (company) => {
    setLoading(true);
    try {
      await axios.put(`/companies/${company.id}`, {
        name: company.name,
        is_active: !company.is_active,
      });
      toast.success("Status updated");
      fetchCompanies();
    } catch {
      toast.error("Failed to update status");
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ id: null, name: "", is_active: true });
    setEditing(false);
  };

  const filtered = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Manage Companies</h2>

      {/* Search */}
      <input
        type="text"
        placeholder="Search companies..."
        className="border p-2 mb-4 w-full rounded"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Form */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-6 items-center">
        <input
          type="text"
          placeholder="Company name"
          className="border p-2 rounded w-full col-span-2"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        {editing && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <span>Active</span>
          </label>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            className={`bg-blue-600 text-white px-4 py-2 rounded ${
              loading ? "opacity-50" : "hover:bg-blue-700"
            }`}
            disabled={loading}
          >
            {editing ? "Update" : "Add"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Loading Spinner */}
      {loading && (
        <div className="flex justify-center my-4">
          <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Company List */}
      {!loading && filtered.length > 0 ? (
        <div className="bg-white rounded shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100 text-left text-sm font-semibold">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <motion.tr
                  key={c.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="border-t hover:bg-gray-50 text-sm"
                >
                  <td className="px-4 py-2">{c.name}</td>
                  <td className="px-4">
                    <div className="flex items-center gap-1">
                      {c.is_active ? (
                        <FiCheckCircle className="text-green-600" />
                      ) : (
                        <FiXCircle className="text-red-500" />
                      )}
                      <span>{c.is_active ? "Active" : "Inactive"}</span>
                    </div>
                  </td>
                  <td className="flex gap-4 justify-center px-4 py-2">
                    <button
                      onClick={() => handleEdit(c)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <FiEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && <p className="text-gray-500 italic">No companies found.</p>
      )}
    </div>
  );
}
